import React, { useState, useEffect } from 'react';
import { Button, Form, ListGroup, Tab, Tabs, Badge, Row, Col, Alert, Spinner } from '@/components/ui';
import ResponsiveModal from '../../../components/ResponsiveModal';
import type { User } from '../../../types/User';
import type { ForemanArea } from '../../../types/ForemanArea';
import axios from 'axios';
import type { Role } from '../../../types/Role';
import type { Classification } from '../../../types/Employee';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../contexts/AuthContext';
import { usersService } from '../../../services/usersService';
import { employeeService } from '../../../services/employeeService';
import { classificationService } from '../../../services/classificationService';

interface UserModalProps {
  show: boolean;
  onHide: () => void;
  user: User | null;
  onSave: (userData: Partial<User>) => Promise<User>;
}

const UserModal: React.FC<UserModalProps> = ({ show, onHide, user, onSave }) => {
  const [activeTab, setActiveTab] = useState('user-info');
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    email: '',
    employeeNumber: '',
    roleId: 0,
    roleName: '',
    status: 'ACTIVE' as 'ACTIVE' | 'DISABLED',
    hasPortalAccess: false,
    defaultClassificationId: 0,
    employeeClass: '',
    summerShiftLength: 0,
    winterShiftLength: 0,
    isRoadPatroller: false,
  });


  const [foremanAreaIds, setForemanAreaIds] = useState<number[]>([]);
  const [availableAreas, setAvailableAreas] = useState<ForemanArea[]>([]);
  const [selectedAreas, setSelectedAreas] = useState<ForemanArea[]>([]);
  const [defaultForemanAreaId, setDefaultForemanAreaId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<Role[]>([]);
  const [allRoles, setAllRoles] = useState<Role[]>([]); // Store all roles for filtering
  const [classifications, setClassifications] = useState<Classification[]>([]);
  const [roleClassifications, setRoleClassifications] = useState<Classification[]>([]);
  const [allRoleClassifications, setAllRoleClassifications] = useState<Classification[]>([]); // Store all role classifications for filtering
  const { hasPermission } = usePermissions();
  const { currentUser } = useAuth();
  const isReadOnly = !hasPermission('user:edit') && !hasPermission('employee:edit');
  
  // Check if current user is HR (Human Resources role)
  const isHR = currentUser?.role?.name?.toLowerCase() === 'human resources';
  
  // Filter out Admin role if current user is HR
  const availableRoles = isHR 
    ? roles.filter(role => role.name.toLowerCase() !== 'admin')
    : roles;
  const isAdmin = hasPermission('employee:portal_manage'); // Only users with portal_manage permission can manage portal access
  const canEditAreas = hasPermission('user:edit') || hasPermission('employee:edit'); // HR and Admins can edit foreman areas
  const isCreateMode = !user;

  // Reset form when modal opens/closes
  const resetForm = async () => {
    if (user) {
      const userRoleId = user.roleId || 0;
      const userClassificationId = user.defaultClassificationId || 0;
      
      setFormData({
        name: user.name || '',
        lastName: user.lastName || '',
        email: user.email,
        employeeNumber: user.employeeNumber || '',
        roleId: userRoleId,
        roleName: '', // Deprecated - using roleId from dropdown instead
        status: user.status || 'ACTIVE',
        hasPortalAccess: user.hasPortalAccess || false,
        defaultClassificationId: userClassificationId,
        employeeClass: user.defaultClassification?.code || '',
        summerShiftLength: user.summerShiftLength || 0,
        winterShiftLength: user.winterShiftLength || 0,
        isRoadPatroller: user.isRoadPatroller || false,
      });
      setForemanAreaIds(user.foremanAreas?.map(fa => fa.id) || []);
      setSelectedAreas(user.foremanAreas || []);
      // Set default foreman area from user data, or auto-set if only one area
      const userDefaultAreaId = (user as any).defaultForemanAreaId || (user as any).defaultForemanArea?.id;
      if (userDefaultAreaId) {
        setDefaultForemanAreaId(userDefaultAreaId);
      } else if (user.foremanAreas && user.foremanAreas.length === 1) {
        setDefaultForemanAreaId(user.foremanAreas[0].id);
      } else {
        setDefaultForemanAreaId(null);
      }
      
      // Filter roles based on user's foreman areas
      if (user.foremanAreas && user.foremanAreas.length > 0) {
        // Wait for allRoles to be loaded before filtering
        if (allRoles.length > 0) {
          filterRolesByForemanAreas(allRoles, user.foremanAreas);
        }
      }
      
      // Filter roles based on user's foreman areas (will be applied when allRoles loads)
      // The useEffect will handle this automatically when selectedAreas and allRoles are set
      
      // Fetch classifications for the user's role
      if (userRoleId > 0) {
        await fetchClassificationsForRole(userRoleId, userClassificationId);
      }
    } else {
      setFormData({
        name: '',
        lastName: '',
        email: '',
        employeeNumber: '',
        roleId: 0,
        roleName: '',
        status: 'ACTIVE',
        hasPortalAccess: false,
        defaultClassificationId: 0,
        employeeClass: '',
        summerShiftLength: 0,
        winterShiftLength: 0,
        isRoadPatroller: false,
      });
      setForemanAreaIds([]);
      setSelectedAreas([]);
      setDefaultForemanAreaId(null);
      setRoleClassifications([]);
    }
    setActiveTab('user-info');
  };

  useEffect(() => {
    if (show) {
      fetchInitialData();
      resetForm(); // resetForm is now async but we don't need to await it
    }
  }, [show, user]);

  // Fetch classifications when roleId changes (for existing users)
  useEffect(() => {
    if (formData.roleId && formData.roleId > 0) {
      // Pass current classificationId to prevent auto-selection when loading existing user
      fetchClassificationsForRole(formData.roleId, formData.defaultClassificationId);
    } else {
      setRoleClassifications([]);
      setAllRoleClassifications([]);
    }
  }, [formData.roleId]);

  // Filter classifications when default foreman area changes
  useEffect(() => {
    if (allRoleClassifications.length > 0 && formData.roleId && formData.roleId > 0) {
      // Only auto-select if we're not loading an existing user (check if classification is already set)
      // If user has a classification set, preserve it (don't auto-select)
      const shouldAutoSelect = !formData.defaultClassificationId || formData.defaultClassificationId === 0;
      filterClassificationsByDefaultForemanArea(allRoleClassifications, shouldAutoSelect);
    }
  }, [defaultForemanAreaId, allRoleClassifications, formData.roleId, formData.defaultClassificationId]);

  // Filter roles when selectedAreas change
  useEffect(() => {
    if (allRoles.length > 0 && selectedAreas.length > 0) {
      filterRolesByForemanAreas(allRoles, selectedAreas);
    } else if (selectedAreas.length === 0) {
      setRoles([]);
    }
  }, [selectedAreas, allRoles]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchForemanAreas(),
        fetchRoles(),
        fetchClassifications()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchForemanAreas = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/foreman-areas`);
      setAvailableAreas(response.data);
    } catch (error) {
      console.error('Error fetching foreman areas:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/roles?page=1&limit=1000`);
      const rolesData = response.data.roles || response.data;
      const allRolesArray = Array.isArray(rolesData) ? rolesData : [];
      setAllRoles(allRolesArray);
      // Filter roles based on selected foreman areas
      filterRolesByForemanAreas(allRolesArray, selectedAreas);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setAllRoles([]);
      setRoles([]);
    }
  };

  // Filter roles based on selected foreman areas' service areas
  const filterRolesByForemanAreas = (rolesToFilter: Role[], foremanAreas: ForemanArea[]) => {
    if (foremanAreas.length === 0) {
      setRoles([]);
      return;
    }

    // Get unique service area IDs from selected foreman areas
    const serviceAreaIds = new Set(
      foremanAreas
        .map(area => area.serviceAreaId)
        .filter(id => id !== undefined && id !== null) as number[]
    );

    if (serviceAreaIds.size === 0) {
      setRoles([]);
      return;
    }

    // Filter roles that have at least one classification in any of the selected service areas
    const filteredRoles = rolesToFilter.filter(role => {
      if (!role.roleClassifications || role.roleClassifications.length === 0) {
        // If role has no classifications, include it (some roles might not have classifications)
        return true;
      }
      // Check if any classification belongs to one of the selected service areas
      return role.roleClassifications.some(rc => 
        rc.classification?.serviceArea?.id && serviceAreaIds.has(rc.classification.serviceArea.id)
      );
    });

    setRoles(filteredRoles);
  };

  const fetchClassifications = async () => {
    try {
      const response = await classificationService.getClassifications(1, 1000);
      setClassifications(response.classifications || []);
    } catch (error) {
      console.error('Error fetching classifications:', error);
      setClassifications([]);
    }
  };

  // Fetch classifications for selected role
  const fetchClassificationsForRole = async (roleId: number, currentClassificationId?: number) => {
    if (!roleId || roleId === 0) {
      setRoleClassifications([]);
      setAllRoleClassifications([]);
      return;
    }
    
    try {
      const allClassifications = await classificationService.getClassificationsForRole(roleId);
      setAllRoleClassifications(allClassifications);
      
      // Filter by default foreman area's service area if available
      // Only auto-select if we're not loading an existing user (currentClassificationId would be set)
      const shouldAutoSelect = !currentClassificationId;
      filterClassificationsByDefaultForemanArea(allClassifications, shouldAutoSelect);
    } catch (error) {
      console.error('Error fetching classifications for role:', error);
      setRoleClassifications([]);
      setAllRoleClassifications([]);
    }
  };

  // Filter classifications by default foreman area's service area
  const filterClassificationsByDefaultForemanArea = (classificationsToFilter: Classification[], shouldAutoSelect: boolean = true) => {
    if (!formData.roleId || formData.roleId === 0) {
      setRoleClassifications([]);
      return;
    }

    const currentClassificationId = formData.defaultClassificationId;
    // Find the user's current classification if it exists
    const currentClassification = currentClassificationId > 0 
      ? classificationsToFilter.find(c => c.id === currentClassificationId)
      : null;

    // If no default foreman area, show all role classifications but don't auto-select if user has existing classification
    if (!defaultForemanAreaId) {
      setRoleClassifications(classificationsToFilter);
      // Only auto-select if we have classifications and should auto-select and no existing classification
      if (shouldAutoSelect && classificationsToFilter.length > 0 && currentClassificationId === 0) {
        const selectedRole = roles.find(r => r.id === formData.roleId);
        const isAdminRole = selectedRole?.name?.toLowerCase() === 'admin';
        
        // For Admin role, try to find "N/A" classification, otherwise use first
        let defaultClassification = classificationsToFilter[0];
        if (isAdminRole) {
          const naClassification = classificationsToFilter.find(c => 
            c.code?.toUpperCase() === 'N/A' || c.code?.toUpperCase() === 'NA'
          );
          if (naClassification) {
            defaultClassification = naClassification;
          }
        }
        
        setFormData(prev => ({
          ...prev,
          defaultClassificationId: defaultClassification.id,
          employeeClass: defaultClassification.code || ''
        }));
      }
      return;
    }

    // Find the default foreman area
    const defaultForemanArea = selectedAreas.find(a => a.id === defaultForemanAreaId);
    if (!defaultForemanArea || !defaultForemanArea.serviceAreaId) {
      setRoleClassifications(classificationsToFilter);
      return;
    }

    // Filter classifications that belong to the default foreman area's service area
    let filteredClassifications = classificationsToFilter.filter(classification => 
      classification.serviceAreaId === defaultForemanArea.serviceAreaId
    );

    // If user has an existing classification that doesn't match the filter, include it in the list
    // This ensures existing employees' classifications are always visible
    if (currentClassification && !filteredClassifications.find(c => c.id === currentClassificationId)) {
      filteredClassifications = [currentClassification, ...filteredClassifications];
    }

    setRoleClassifications(filteredClassifications);

    // If user has an existing classification that's in the filtered list, preserve it (don't auto-select)
    if (currentClassificationId > 0 && currentClassification && filteredClassifications.some(c => c.id === currentClassificationId)) {
      // User has an existing classification that's valid - don't override it
      return; // Exit early to preserve existing classification
    }
    
    // Auto-select classification if needed (only when shouldAutoSelect is true)
    if (shouldAutoSelect && filteredClassifications.length > 0) {
      // Check if current classification is still valid (either in filtered list or is the current one we preserved)
      const isCurrentValid = currentClassificationId > 0 && 
        filteredClassifications.some(c => c.id === currentClassificationId);
      
      // Always auto-select if no classification is selected, or if current is not valid
      if (!isCurrentValid || currentClassificationId === 0) {
        // Current classification is not valid, auto-select a new one
        // Prefer classifications that match the default foreman area's service area
        const matchingClassifications = filteredClassifications.filter(c => 
          c.serviceAreaId === defaultForemanArea.serviceAreaId
        );
        const classificationsToChooseFrom = matchingClassifications.length > 0 
          ? matchingClassifications 
          : filteredClassifications;
        
        const selectedRole = roles.find(r => r.id === formData.roleId);
        const isAdminRole = selectedRole?.name?.toLowerCase() === 'admin';
        
        // For Admin role, try to find "N/A" classification, otherwise use first
        let defaultClassification = classificationsToChooseFrom[0];
        if (isAdminRole) {
          const naClassification = classificationsToChooseFrom.find(c => 
            c.code?.toUpperCase() === 'N/A' || c.code?.toUpperCase() === 'NA'
          );
          if (naClassification) {
            defaultClassification = naClassification;
          }
        }
        
        setFormData(prev => ({
          ...prev,
          defaultClassificationId: defaultClassification.id,
          employeeClass: defaultClassification.code || ''
        }));
      }
    } else if (filteredClassifications.length === 0 && shouldAutoSelect) {
      // No classifications for this service area - show all and try to auto-select from all
      setRoleClassifications(classificationsToFilter);
      if (classificationsToFilter.length > 0 && currentClassificationId === 0) {
        const selectedRole = roles.find(r => r.id === formData.roleId);
        const isAdminRole = selectedRole?.name?.toLowerCase() === 'admin';
        
        let defaultClassification = classificationsToFilter[0];
        if (isAdminRole) {
          const naClassification = classificationsToFilter.find(c => 
            c.code?.toUpperCase() === 'N/A' || c.code?.toUpperCase() === 'NA'
          );
          if (naClassification) {
            defaultClassification = naClassification;
          }
        }
        
        setFormData(prev => ({
          ...prev,
          defaultClassificationId: defaultClassification.id,
          employeeClass: defaultClassification.code || ''
        }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isReadOnly) return;
    
    // Validate form data
    if (!formData.name || !formData.lastName || !formData.email || !formData.roleId || formData.roleId === 0) {
      alert('Please fill in all required fields (First Name, Last Name, Email, Role)');
      return;
    }

    if (!formData.hasPortalAccess && !formData.employeeNumber.trim()) {
      alert('Employee number is required when portal access is not enabled.');
      return;
    }

    if (formData.employeeNumber.trim()) {
      try {
        const check = await employeeService.checkEmployeeNumber(
          formData.employeeNumber.trim(),
          user?.id
        );
        if (!check.isUnique) {
          alert(`Employee number "${formData.employeeNumber}" is already assigned to ${check.existingEmployee?.name || 'another employee'}. Please use a different employee number.`);
          return;
        }
      } catch (checkError: any) {
        console.error('Error checking employee number uniqueness:', checkError);
        alert('Could not verify employee number uniqueness. Please try again.');
        return;
      }
    }

    // Validate default classification is selected
    if (!formData.defaultClassificationId || formData.defaultClassificationId === 0) {
      alert('Please select a Default Employee Classification. This field is required.');
      return;
    }

    // Prevent HR from assigning Admin role
    if (isHR) {
      const selectedRole = roles.find(r => r.id === formData.roleId);
      if (selectedRole && selectedRole.name.toLowerCase() === 'admin') {
        alert('Human Resources cannot assign or change the Admin role. Please select a different role.');
        return;
      }
    }

    // Validate that Active employees have at least one Foreman Area
    if (formData.status === 'ACTIVE' && selectedAreas.length === 0) {
      alert('Active employees must have at least one Foreman Area assigned. Please assign a Foreman Area before saving.');
      return;
    }

    // Validate Dawson email if portal access is enabled
    if (formData.hasPortalAccess && isAdmin) {
      const email = formData.email.toLowerCase();
      if (!email.includes('@dawson')) {
        alert('Portal access requires a Dawson email address (must contain "@dawson"). Please update the email address or disable portal access.');
        return;
      }
    }
    
    try {
      // Use roleId directly from the dropdown
      const roleId = formData.roleId;

      // Use defaultClassificationId directly from dropdown
      const defaultClassificationId = formData.defaultClassificationId;

      const trimmedNum = formData.employeeNumber?.trim();
      const employeeNumberPayload = formData.hasPortalAccess
        ? trimmedNum
          ? trimmedNum
          : null
        : trimmedNum || undefined;

      const userData: any = {
        name: formData.name,
        lastName: formData.lastName,
        email: formData.email,
        employeeNumber: employeeNumberPayload,
        roleId: roleId || undefined,
        status: formData.status,
        defaultClassificationId: defaultClassificationId,
        summerShiftLength: formData.summerShiftLength !== undefined && formData.summerShiftLength !== null ? formData.summerShiftLength : undefined,
        winterShiftLength: formData.winterShiftLength !== undefined && formData.winterShiftLength !== null ? formData.winterShiftLength : undefined,
        foremanAreaIds: selectedAreas.map(area => area.id),
        defaultForemanAreaId: defaultForemanAreaId || undefined,
        isRoadPatroller: formData.isRoadPatroller,
        hasPortalAccess: formData.hasPortalAccess
      };

      const savedUser = await onSave(userData);
      
      // Update portal info separately if Admin and portal access is enabled
      // Use the email address, role, and foreman areas from Employee Info for portal login
      if (isAdmin && formData.hasPortalAccess) {
        const userId = user?.id || savedUser?.id;
        if (userId) {
          await usersService.updatePortalInfo(userId, {
            portalEmail: formData.email, // Use email from Employee Info
            portalRoleId: formData.roleId || undefined, // Use role from Employee Info
            foremanAreaIds: selectedAreas.map(area => area.id), // Keep foreman areas in sync 
            defaultForemanAreaId: defaultForemanAreaId || undefined
          });
        }
      }

      onHide();
    } catch (error: any) {
      console.error('Error saving user:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save user. Please try again.';
      alert(errorMessage);
    }
  };

  const handleAreaSelect = (area: ForemanArea) => {
    if (isReadOnly || !canEditAreas) return;
    if (!selectedAreas.find(a => a.id === area.id)) {
      const newSelectedAreas = [...selectedAreas, area];
      const newForemanAreaIds = [...foremanAreaIds, area.id];
      setSelectedAreas(newSelectedAreas);
      setForemanAreaIds(newForemanAreaIds);
      
      // Auto-set default if this is the first/only area
      if (newSelectedAreas.length === 1) {
        setDefaultForemanAreaId(area.id);
        // Filter and auto-select classification based on new default foreman area
        // This will be handled by the useEffect when defaultForemanAreaId changes
      }

      // Filter roles based on new foreman areas
      filterRolesByForemanAreas(allRoles, newSelectedAreas);
      
      // In create mode, clear role/classification if they are no longer valid.
      // In update mode, preserve current values so "remove all then add back" does not wipe fields.
      if (isCreateMode && formData.roleId > 0) {
        const serviceAreaIds = new Set(
          newSelectedAreas
            .map(a => a.serviceAreaId)
            .filter(id => id !== undefined && id !== null) as number[]
        );
        const currentRole = allRoles.find(r => r.id === formData.roleId);
        const isRoleValid = currentRole && (
          !currentRole.roleClassifications || currentRole.roleClassifications.length === 0 ||
          currentRole.roleClassifications.some(rc =>
            rc.classification?.serviceArea?.id && serviceAreaIds.has(rc.classification.serviceArea.id)
          )
        );

        if (!isRoleValid) {
          setFormData(prev => ({
            ...prev,
            roleId: 0,
            defaultClassificationId: 0,
            employeeClass: ''
          }));
          setRoleClassifications([]);
        }
      }
    }
  };

  const handleAreaRemove = (area: ForemanArea) => {
    if (isReadOnly || !canEditAreas) return;
    const newSelectedAreas = selectedAreas.filter(a => a.id !== area.id);
    const newForemanAreaIds = foremanAreaIds.filter(id => id !== area.id);
    setSelectedAreas(newSelectedAreas);
    setForemanAreaIds(newForemanAreaIds);
    
    // Clear default if removed area was the default
    if (defaultForemanAreaId === area.id) {
      // Auto-set default to the only remaining area, or clear if none
      if (newSelectedAreas.length === 1) {
        setDefaultForemanAreaId(newSelectedAreas[0].id);
        // Filter and auto-select classification based on new default foreman area
        if (allRoleClassifications.length > 0) {
          filterClassificationsByDefaultForemanArea(allRoleClassifications, true);
        }
      } else {
        setDefaultForemanAreaId(null);
        // Show all role classifications when no default foreman area
        if (allRoleClassifications.length > 0) {
          filterClassificationsByDefaultForemanArea(allRoleClassifications, true);
        }
      }
    }

    // Filter roles based on remaining foreman areas
    filterRolesByForemanAreas(allRoles, newSelectedAreas);
    
    // Clear role and classification in create mode only.
    // For updates, preserve role/classification while areas are temporarily empty.
    if (newSelectedAreas.length === 0) {
      if (isCreateMode) {
        setFormData(prev => ({
          ...prev,
          roleId: 0,
          defaultClassificationId: 0,
          employeeClass: ''
        }));
        setRoleClassifications([]);
      }
    } else if (formData.roleId > 0) {
      // Check if current role is still valid
      const serviceAreaIds = new Set(
        newSelectedAreas
          .map(a => a.serviceAreaId)
          .filter(id => id !== undefined && id !== null) as number[]
      );
      const currentRole = allRoles.find(r => r.id === formData.roleId);
      const isRoleValid = currentRole && (
        !currentRole.roleClassifications || currentRole.roleClassifications.length === 0 ||
        currentRole.roleClassifications.some(rc => 
          rc.classification?.serviceArea?.id && serviceAreaIds.has(rc.classification.serviceArea.id)
        )
      );
      
      if (isCreateMode && !isRoleValid) {
        setFormData(prev => ({
          ...prev,
          roleId: 0,
          defaultClassificationId: 0,
          employeeClass: ''
        }));
        setRoleClassifications([]);
      }
    }
  };

  const handleDefaultAreaChange = (areaId: number, isChecked: boolean) => {
    if (isReadOnly || !canEditAreas) return;
    if (isChecked) {
      setDefaultForemanAreaId(areaId);
      // Filter and auto-select classification based on new default foreman area
      if (allRoleClassifications.length > 0) {
        filterClassificationsByDefaultForemanArea(allRoleClassifications, true);
      }
    } else {
      setDefaultForemanAreaId(null);
      // Show all role classifications when no default foreman area
      if (allRoleClassifications.length > 0) {
        filterClassificationsByDefaultForemanArea(allRoleClassifications, true);
      }
    }
  };

  const unassignedAreas = availableAreas.filter(
    area => !selectedAreas.find(selected => selected.id === area.id)
  );

  const groupAreasByServiceArea = (areas: ForemanArea[]) => {
    const groups = areas.reduce((groups, area) => {
      const serviceAreaName = area.serviceArea?.name || 'Unassigned';
      if (!groups[serviceAreaName]) {
        groups[serviceAreaName] = [];
      }
      groups[serviceAreaName].push(area);
      return groups;
    }, {} as { [key: string]: ForemanArea[] });
    
    // Sort areas within each group by name
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => a.name.localeCompare(b.name));
    });
    
    return groups;
  };

  // Helper function to sort service area groups consistently
  const sortServiceAreaGroups = (groups: { [key: string]: ForemanArea[] }) => {
    return Object.entries(groups).sort(([nameA], [nameB]) => {
      // Extract numbers from service area names (e.g., "Service Area 25" -> 25)
      const numA = nameA.match(/\d+/)?.[0];
      const numB = nameB.match(/\d+/)?.[0];
      
      if (numA && numB) {
        // If both have numbers, sort numerically
        return parseInt(numA) - parseInt(numB);
      } else if (numA) {
        // Numbers come before non-numbers
        return -1;
      } else if (numB) {
        return 1;
      } else {
        // Both are non-numeric, sort alphabetically
        return nameA.localeCompare(nameB);
      }
    });
  };

  if (loading) {
    return (
      <ResponsiveModal isOpen={show} onClose={onHide} title={user ? 'Edit User' : 'Add User'} lockDocument={false}>
        <div className="tw-text-center tw-py-4">
          <Spinner>Loading...</Spinner>
        </div>
      </ResponsiveModal>
    );
  }

  const handleCancel = () => {
    resetForm();
    onHide();
  };

  // Check if form is valid for saving
  const isFormValid = () => {
    // Basic required fields
    if (!formData.name || !formData.lastName || !formData.email || !formData.roleId || formData.roleId === 0) {
      return false;
    }
    if (!formData.hasPortalAccess && !formData.employeeNumber.trim()) {
      return false;
    }
    // Active employees must have at least one Foreman Area
    if (formData.status === 'ACTIVE' && selectedAreas.length === 0) {
      return false;
    }
    return true;
  };

  const modalFooter = (
    <>
      <Button variant="secondary" type="button" onClick={handleCancel} className="tw-mr-2">
        Cancel
      </Button>
      {!isReadOnly && (
        <Button 
          variant="primary" 
          type="submit" 
          form="userForm"
          disabled={!isFormValid()}
        >
          {user ? 'Save Changes' : 'Save'}
        </Button>
      )}
    </>
  );

  return (
    <ResponsiveModal
      isOpen={show}
      onClose={onHide}
      title={user ? 'Edit Employee' : '+ Add Employee'}
      footer={modalFooter}
      size="large"
      lockDocument={false}
    >
      <Form onSubmit={handleSubmit} id="userForm">
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => k && setActiveTab(k)}
          className="tw-mb-3"
        >
          <Tab eventKey="user-info" title="Employee Info">
            <Row className="tw-mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>First Name <span className="tw-text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={e => {
                      if (isReadOnly) return;
                      setFormData({...formData, name: e.target.value});
                    }}
                    readOnly={isReadOnly}
                    required
                    placeholder="First Name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Last Name <span className="tw-text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                    value={formData.lastName}
                onChange={e => {
                  if (isReadOnly) return;
                      setFormData({...formData, lastName: e.target.value});
                }}
                readOnly={isReadOnly}
                required
                    placeholder="Last Name"
              />
            </Form.Group>
              </Col>
            </Row>

            <Form.Group className="tw-mb-3">
              <Form.Check
                type="checkbox"
                id="isActive"
                label="Active"
                checked={formData.status === 'ACTIVE'}
                onChange={e => !isReadOnly && setFormData({...formData, status: e.target.checked ? 'ACTIVE' : 'DISABLED'})}
                disabled={isReadOnly}
              />
            </Form.Group>

            <Row className="tw-mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Email Address <span className="tw-text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={e => !isReadOnly && setFormData({...formData, email: e.target.value})}
                    readOnly={isReadOnly}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    Employee Number {!formData.hasPortalAccess && <span className="tw-text-danger">*</span>}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.employeeNumber}
                    onChange={e => !isReadOnly && setFormData({...formData, employeeNumber: e.target.value})}
                    readOnly={isReadOnly}
                    placeholder="Enter employee number"
                    required={!formData.hasPortalAccess}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="tw-mb-3">
              <Col md={6}>
                <Form.Group>
              <Form.Label>Summer Shift Length</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={formData.summerShiftLength ?? ''}
                onChange={e => !isReadOnly && setFormData({...formData, summerShiftLength: parseFloat(e.target.value) || 0})}
                readOnly={isReadOnly}
                placeholder="e.g. 8.25 hours"
              />
            </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
              <Form.Label>Winter Shift Length</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={formData.winterShiftLength ?? ''}
                onChange={e => !isReadOnly && setFormData({...formData, winterShiftLength: parseFloat(e.target.value) || 0})}
                readOnly={isReadOnly}
                placeholder="e.g. 8.25 hours"
              />
            </Form.Group>
              </Col>
            </Row>

            <Form.Group className="tw-mb-3">
              <Form.Check
                type="checkbox"
                id="isRoadPatroller"
                label="Road Patroller"
                checked={formData.isRoadPatroller}
                onChange={e => !isReadOnly && setFormData({...formData, isRoadPatroller: e.target.checked})}
                disabled={isReadOnly}
              />
              <Form.Text className="tw-text-content-muted">
                Indicates that this employee is a road patroller.
              </Form.Text>
            </Form.Group>

            <Form.Group className="tw-mb-3">
              <Form.Check
                type="checkbox"
                id="hasPortalAccess"
                label="Enable Portal Login Access"
                checked={formData.hasPortalAccess}
                onChange={e => {
                  if (!isAdmin) return; // Only admins can change this
                  
                  // Validate Dawson email before enabling portal access
                  if (e.target.checked) {
                    const email = formData.email.toLowerCase();
                    if (!email.includes('@dawson')) {
                      alert('Portal access requires a Dawson email address (must contain "@dawson"). Please update the email address before enabling portal access.');
                      return;
                    }
                  }
                  
                  setFormData({...formData, hasPortalAccess: e.target.checked});
                }}
                disabled={isReadOnly || !isAdmin}
              />
              <Form.Text className="tw-text-content-muted">
                {isAdmin
                  ? 'When enabled, portal access will be created using the Email Address above. Only Administrators can manage portal access. Email must be a Dawson email address.' 
                  : 'Portal access can only be managed by Administrators'}
              </Form.Text>
            </Form.Group>

          </Tab>

          <Tab eventKey="foreman-areas" title="Access">
            {!canEditAreas && (
              <Alert variant="info" className="tw-mb-3">
                <i className="bi bi-info-circle tw-mr-2"></i>
                Access assignments can only be managed by HR or Administrators.
              </Alert>
            )}

            {formData.status === 'ACTIVE' && selectedAreas.length === 0 && (
              <Alert variant="warning" className="tw-mb-3">
                <i className="bi bi-exclamation-triangle-fill tw-mr-2"></i>
                <strong>Required:</strong> Active employees must have at least one area assigned. Please assign an area below before saving.
              </Alert>
            )}

            <Row className="tw-mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Role <span className="tw-text-danger">*</span></Form.Label>
                  <Form.Select
                    value={formData.roleId > 0 ? formData.roleId : ''}
                    onChange={e => {
                      if (isReadOnly) return;
                      const selectedRoleId = e.target.value ? parseInt(e.target.value) : 0;
                      setFormData({...formData, roleId: selectedRoleId, defaultClassificationId: 0, employeeClass: ''});
                      fetchClassificationsForRole(selectedRoleId);
                    }}
                    disabled={isReadOnly || selectedAreas.length === 0}
                    required
                  >
                    <option value="">
                      {selectedAreas.length === 0 ? 'Select Access area first' : 'Select a role'}
                    </option>
                    {availableRoles.map(role => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </Form.Select>
                  {selectedAreas.length === 0 && (
                    <Form.Text className="tw-text-content-muted">
                      Please select at least one Access area before selecting a Role.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Default Employee Classification <span className="tw-text-danger">*</span></Form.Label>
                  <Form.Select
                    value={formData.roleId > 0 && formData.defaultClassificationId > 0 ? String(formData.defaultClassificationId) : ''}
                    onChange={e => {
                      const classificationId = e.target.value ? parseInt(e.target.value, 10) : 0;
                      const selectedClassification = roleClassifications.find(c => c.id === classificationId);
                      setFormData({
                        ...formData,
                        defaultClassificationId: classificationId,
                        employeeClass: selectedClassification?.code || ''
                      });
                    }}
                    disabled={isReadOnly || !formData.roleId || formData.roleId === 0 || roleClassifications.length === 0}
                    required
                  >
                    <option value="">
                      {formData.roleId > 0 ? 'Select a classification' : 'Select a role first'}
                    </option>
                    {roleClassifications.map(classification => (
                      <option key={classification.id} value={classification.id}>
                        {classification.code} - ${classification.rate?.toFixed(2) || '0.00'} {classification.serviceArea?.name ? `(${classification.serviceArea.name})` : ''}
                      </option>
                    ))}
                  </Form.Select>
                  {formData.roleId > 0 && roleClassifications.length === 0 && (
                    <Form.Text className="tw-text-content-muted">
                      No classifications available for this role. Please assign classifications to this role first.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="tw-mb-3">
              <Form.Label>Access Area Assignment</Form.Label>
              <Form.Text className="tw-text-content-muted tw-block tw-mb-3">
                Assign areas to this employee. This determines which areas the employee can access for work assignments.
              </Form.Text>
              <Row>
                <Col md={5}>
                  <h6>Available Areas</h6>
                  <ListGroup style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {sortServiceAreaGroups(groupAreasByServiceArea(unassignedAreas)).map(([serviceArea, areas]) => (
                      <div key={serviceArea}>
                        <ListGroup.Item variant="secondary">
                          {serviceArea}
                        </ListGroup.Item>
                        {areas.map(area => (
                          <ListGroup.Item
                            key={area.id}
                            action={canEditAreas}
                            onClick={() => canEditAreas && handleAreaSelect(area)}
                            className="tw-pl-4"
                            style={{
                              cursor: canEditAreas ? 'pointer' : 'default',
                              opacity: canEditAreas ? 1 : 0.6
                            }}
                          >
                            {area.name}
                          </ListGroup.Item>
                        ))}
                      </div>
                    ))}
                  </ListGroup>
                </Col>
                <Col md={2} className="tw-flex tw-items-center tw-justify-center">
                  <div className="tw-text-center">
                    <i className="bi bi-arrow-left-right"></i>
                  </div>
                </Col>
                <Col md={5}>
                  <h6>Assigned Areas</h6>
                  <ListGroup style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {selectedAreas.length === 0 ? (
                      <ListGroup.Item className="tw-text-content-muted tw-text-center tw-py-4">
                        No areas assigned
                      </ListGroup.Item>
                    ) : (
                      sortServiceAreaGroups(groupAreasByServiceArea(selectedAreas)).map(([serviceArea, areas]) => (
                        <div key={serviceArea}>
                          <ListGroup.Item variant="secondary">
                            {serviceArea}
                          </ListGroup.Item>
                          {areas.map(area => (
                            <ListGroup.Item
                              key={area.id}
                              className="tw-pl-4 tw-flex tw-justify-between tw-items-center"
                              style={{
                                cursor: canEditAreas ? 'default' : 'default',
                                opacity: canEditAreas ? 1 : 0.6
                              }}
                            >
                              <div
                                className="tw-grow"
                                onClick={() => canEditAreas && handleAreaRemove(area)}
                                style={{ cursor: canEditAreas ? 'pointer' : 'default' }}
                            >
                              {area.name}
                              </div>
                              {canEditAreas && selectedAreas.length > 1 && (
                                <Form.Check
                                  type="checkbox"
                                  checked={defaultForemanAreaId === area.id}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleDefaultAreaChange(area.id, e.target.checked);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  label="Default"
                                  className="tw-ml-2"
                                />
                              )}
                            </ListGroup.Item>
                          ))}
                        </div>
                      ))
                    )}
                  </ListGroup>
                </Col>
              </Row>
            </Form.Group>
          </Tab>
        </Tabs>
      </Form>
    </ResponsiveModal>
  );
};

export default UserModal;
